'use client'

import { useEffect, useMemo, useState } from 'react'

type ObserverRect = Omit<DOMRectReadOnly, 'toJSON'>

const DEFAULT_STATE: ObserverRect = { x: 0, y: 0, width: 0, height: 0, top: 0, left: 0, bottom: 0, right: 0 }

export const useResizeObserver = <T extends HTMLElement = HTMLElement>(options?: ResizeObserverOptions) => {
  const [ref, setRef] = useState<T | null>(null)

  const [rect, setRect] = useState<ObserverRect>(DEFAULT_STATE)
  const [blockSize, setBlockSize] = useState<number>(0)
  const [inlineSize, setInlineSize] = useState<number>(0)

  const observer = useMemo(() => {
    if (typeof window !== 'undefined') {
      return new ResizeObserver(([entry]) => {
        if (entry) {
          setRect(entry.contentRect)
          setBlockSize(entry.borderBoxSize[0].blockSize)
          setInlineSize(entry.borderBoxSize[0].inlineSize)
        }
      })
    }
    return null
  }, [])

  useEffect(() => {
    if (ref) {
      observer?.observe(ref, { box: options?.box })
    }

    return () => {
      observer?.disconnect()
    }
  }, [observer, options?.box, ref])

  return [setRef, rect, blockSize, inlineSize] as const
}
